import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, createContext, useContext, useMemo, cloneElement } from 'react';
import { RemirrorPortals } from '@remirror/extension-react-component';
import { i18n } from '@remirror/i18n';
import { RemirrorType, propIsFunction, addKeyToElement, getElementProps, isRemirrorContextProvider, isRemirrorProvider, isReactDOMElement, oneChildOnly } from '@remirror/react-utils';
import { createThemeVariables, themeStyles } from '@remirror/theme';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import isEqual from 'fast-deep-equal/react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
export { default as useSetState } from 'react-use/lib/useSetState';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import ResizeObserver from 'resize-observer-polyfill';
import { object, isRemirrorManager, RemirrorManager, getLazyArray, invariant, ErrorConstant, isPlainObject, isFunction, shouldUseDomEnvironment, Cast, getDocument, isNullOrUndefined, EMPTY_PARAGRAPH_NODE, isArray, bool, EditorWrapper } from '@remirror/core';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { getPositioner, PositionerExtension, emptyVirtualPosition } from '@remirror/extension-positioner';
import { CorePreset } from '@remirror/preset-core';
import { ReactPreset } from '@remirror/preset-react';
import _toArray from '@babel/runtime/helpers/esm/toArray';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import { useFirstMountState } from 'react-use/lib/useFirstMountState';
import { EditorView } from '@remirror/pm/view';
import { mapProps, ReactSerializer } from '../renderers/dist/react.browser.esm.js';
export { ReactSerializer, RenderTree, gatherDomMethods, mapProps } from '../renderers/dist/react.browser.esm.js';

/**
 * Returning a new object reference guarantees that a before-and-after
 * equivalence check will always be false, resulting in a re-render, even when
 * multiple calls to forceUpdate are batched.
 */

function useForceUpdate() {
  var _useState = useState(object()),
      _useState2 = _slicedToArray(_useState, 2),
      setState = _useState2[1];

  var forceUpdate = useCallback(() => {
    setState(object());
  }, []);
  return forceUpdate;
}
/**
 * Preserves the previous version of a provided value.
 *
 * ```tsx
 * const [isOpen, setOpen] = useState<boolean>(false)
 * const previous = usePrevious(isOpen)
 *
 * return <span onClick={() => setOpen(!isOpen)}>{isOpen && previous === isOpen ? 'Stable' : 'Unstable' }</span>
 * ```
 */

function usePrevious(value) {
  var ref = useRef();
  useEffect(() => void (ref.current = value), [value]);
  return ref.current;
}
var defaultBounds = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0
};
/**
 * Provides the measurements for a react element at the point of layout.
 *
 * @remarks
 *
 * Taken from https://codesandbox.io/embed/lp80n9z7v9
 *
 * ```tsx
 * const [bindRef, { height }] = useMeasure()
 *
 * return <div {...bindRef}>Height: {height}</div>
 * ```
 */

function useMeasure() {
  var ref = useRef(null);

  var _useState3 = useState(defaultBounds),
      _useState4 = _slicedToArray(_useState3, 2),
      bounds = _useState4[0],
      setBounds = _useState4[1];

  useLayoutEffect(() => {
    var resizeObserver = new ResizeObserver((_ref) => {
      var _ref2 = _slicedToArray(_ref, 1),
          entry = _ref2[0];

      return setBounds(entry.contentRect);
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => resizeObserver.disconnect();
  }, []);
  return [{
    ref
  }, bounds];
}
/**
 * The `setState` type for the `useSetState` hook.
 */

/**
 * A `useEffect` function with a warning the provided dependencies are deeply
 * equal.
 */
var useEffectWithWarning = process.env.NODE_ENV === 'production' ? useEffect : (effect, deps) => {
  var ref = useRef();
  var unnecessaryChange = useRef(0);
  useEffectOnce(() => {
    ref.current = deps;
  });
  useUpdateEffect(() => {
    if (!isEqual(deps, ref.current)) {
      unnecessaryChange.current = 0;
      ref.current = deps;
      return;
    }

    unnecessaryChange.current += 1;
  });

  var wrappedEffect = () => {
    if (unnecessaryChange.current >= 1) {
      console.warn("The dependencies passed into your useEffect are deeply equal, but an update has been triggered ".concat(unnecessaryChange.current, " time(s). Please consider `useMemo` to memoize your dependencies to prevent unwanted re-renders."), deps);
    }

    return effect();
  }; // eslint-disable-next-line react-hooks/exhaustive-deps


  useEffect(wrappedEffect, deps);
};

/**
 * Creates a ReactContext for the Remirror editor
 */
var RemirrorContext = /*#__PURE__*/createContext(null);
/**
 * Create the context for the i18n framework used within remirror.
 */

var I18nContext = /*#__PURE__*/createContext({
  i18n,
  locale: 'en'
});

/**
 * Create a react manager with all the default react presets and extensions.
 */
function createReactManager(combined) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var core = options.core,
      react = options.react,
      settings = _objectWithoutProperties(options, ["core", "react"]);

  if (isRemirrorManager(combined)) {
    return combined;
  }

  return RemirrorManager.create(() => [...getLazyArray(combined), new ReactPreset(react), new CorePreset(core)], settings);
}

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * This provides access to the Remirror context using hooks.
 *
 * The first argument which is optional can also be a change handler which is
 * called every time the state updates.
 *
 * @remarks
 *
 * The following example applies the root props to the div.
 * ```ts
 * import { RemirrorProvider, useRemirror } from 'remirror';
 *
 * const HooksComponent = (props) => {
 *   // This pulls the remirror props out from the context.
 *   const { getRootProps } = useRemirror();
 *
 *   return <div {...getRootProps()} />;
 * }
 *
 * class App extends Component {
 *   render() {
 *     return (
 *       <RemirrorProvider>
 *         <HooksComponent />
 *       </RemirrorProvider>
 *     );
 *   }
 * }
 * ```
 *
 * For performance reasons `useRemirror` does not automatically trigger a
 * rerender on every editor update. This allows for you use it in component
 * which don't need to track the latest editor state, without suffering a
 * performance penalty.
 *
 * However if you do want to track whether a command is enabled at the current
 * selection or whether a certain formatting mark (bold) is active at the
 * current selection you can pass through an optional parameter.
 *
 * ```
 * const { active, commands } = useRemirror({ autoUpdate: true });
 *
 * return (
 *   <button style={{ fontWeight: active.bold() ? 'bold' : undefined }}>
 *     B
 *   <button>
 * )
 * ```
 *
 * The above example keep track of whether the current selection is bold on
 * every update to the editor. Updates can be document changes and selection
 * changes.
 *
 * For more control you can also use a handler function as the first parameter
 * to selectively rerender as you see fit.
 *
 * ```
 * const { active, commands } = useRemirror(() => {
 *   if (active.bold() === boldActive) {
 *     return;
 *   }
 *
 *   setBoldActive(active.bold());
 * });
 *
 * const [boldActive, setBoldActive] = useState(active.bold());
 *
 * return (
 *   <button style={{ fontWeight: boldActive ? 'bold' : undefined }}>
 *     B
 *   <button>
 * )
 * ```
 *
 * In this case the component only re-renders when the bold formatting is no
 * longer active.
 */

function useRemirror(handler) {
  var context = useContext(RemirrorContext);
  var forceUpdate = useForceUpdate(); // Throw an error if context doesn't exist.

  !context ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.REACT_PROVIDER_CONTEXT
  }) : invariant(false) : void 0;
  useEffect(() => {
    var updateHandler = handler;

    if (!updateHandler) {
      return;
    }

    if (isPlainObject(updateHandler)) {
      var _updateHandler = updateHandler,
          autoUpdate = _updateHandler.autoUpdate;
      updateHandler = autoUpdate ? () => forceUpdate() : undefined;
    }

    if (!isFunction(updateHandler)) {
      return;
    }

    return context.addHandler('updated', updateHandler);
  }, [handler, context, forceUpdate]);
  return context;
}
function useI18n() {
  var context = useContext(I18nContext); // Throw an error if no context exists.

  !context ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.I18N_CONTEXT
  }) : invariant(false) : void 0;
  return context;
}
/**
 * This is a type alias for creating your own typed version of the remirror
 * method.
 *
 * ```ts
 * import { useRemirror, UseRemirrorType } from 'remirror/react';
 * import { SocialPreset } from 'remirror/preset/social'
 *
 * const useSocialRemirror = useRemirror as UseRemirrorType<SocialPreset>;
 *
 * // With the remirror provider context.
 * const Editor = () => {
 *   const { commands } = useSocialRemirror();
 *
 *   // All available commands are shown with intellisense. Command click to goto the implementation.
 *   commands.toggleBold();
 * }
 * ```
 */

function useExtension(Constructor, optionsOrCallback) {
  var dependencies = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

  var _useRemirror = useRemirror(),
      getExtension = _useRemirror.getExtension;

  var extension = useMemo(() => getExtension(Constructor), [Constructor, getExtension]); // Handle the case where it an options object passed in.

  useEffectWithWarning(() => {
    if (isFunction(optionsOrCallback)) {
      return;
    }

    extension.setOptions(optionsOrCallback);
  }, [extension, optionsOrCallback]);
  useEffectWithWarning(() => {
    if (!isFunction(optionsOrCallback)) {
      return;
    }

    return optionsOrCallback({
      addHandler: extension.addHandler.bind(extension),
      addCustomHandler: extension.addCustomHandler.bind(extension),
      extension
    });
  }, [extension, optionsOrCallback, ...dependencies]);
}
function usePreset(Constructor, optionsOrCallback) {
  var dependencies = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

  var _useRemirror2 = useRemirror(),
      getPreset = _useRemirror2.getPreset;

  var preset = useMemo(() => getPreset(Constructor), [Constructor, getPreset]);
  useEffectWithWarning(() => {
    if (isFunction(optionsOrCallback)) {
      return;
    }

    preset.setOptions(optionsOrCallback);
  }, [preset, optionsOrCallback]);
  useEffectWithWarning(() => {
    if (!isFunction(optionsOrCallback)) {
      return;
    }

    return optionsOrCallback({
      addHandler: preset.addHandler.bind(preset),
      addCustomHandler: preset.addCustomHandler.bind(preset),
      preset
    });
  }, [preset, optionsOrCallback, ...dependencies]);
}

/**
 * A hook for creating the editor manager directly in the react component.
 *
 * @remarks
 *
 * The manager is a singleton and doesn't rerender until `manager.destroy()` is called.
 * You should call this method in a `useEffect`
 *
 * This is intentional. However, it's something that can be addressed
 * if it causes issues.
 *
 * ```tsx
 * import { useExtension } from '@remirror/react';
 * import { PresetCore } from '@remirror/preset-core';
 * import { BoldExtension } from '@remirror/extension-bold';
 *
 * const EditorWrapper = () => {
 *   const manager = useManager([new BoldExtension(), new CorePreset()]);
 *
 *   <RemirrorProvider >
 *     <MyEditor />
 *   </RemirrorProvider>;
 * }
 * ```
 */
function useManager(combined) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var combinedRef = useRef(combined);
  var optionsRef = useRef(options); // The initial manager which will never be updated.

  var initialManager = useMemo(() => {
    return createReactManager(combinedRef.current, optionsRef.current);
  }, []);

  var _useState = useState(initialManager),
      _useState2 = _slicedToArray(_useState, 2),
      manager = _useState2[0],
      setManager = _useState2[1];

  combinedRef.current = combined;
  optionsRef.current = options;
  useEffect(() => {
    return manager.addHandler('destroy', () => {
      // `clone` is used to ensure that that any stale extensions are
      // reinitialized.
      setManager(createReactManager(combinedRef.current, optionsRef.current).clone());
    });
  }, [manager, initialManager]);
  return manager;
}

/**
 * A hook for creating a positioner with the `PositionerExtension`. When an
 * active position exists for the provided positioner it will return an object
 * with the `ref`, `top`, `left`, `bottom`, `right` properties.
 *
 *
 * @remarks
 *
 * Must apply the ref to the component when called.
 *
 * ```ts
 * import { usePositioner } from 'remirror/react';
 *
 * const MenuComponent: FC = () => {
 *   const positions = usePositioner('bubble');
 *
 *   return (
 *     <div style={{ bottom, left }} ref={ref}>
 *       <MenuIcon {...options} />
 *     </div>
 *   );
 * }
 *
 * const Wrapper = () => (
 *   <RemirrorProvider extensions={[]}>
 *     <MenuComponent />
 *   </RemirrorProvider>
 * )
 * ```
 */
function usePositioner(positioner) {
  var positions = useMultiPositioner(positioner);

  if (positions.length > 0) {
    return _objectSpread(_objectSpread({}, positions[0]), {}, {
      active: true
    });
  }

  return {
    active: false
  };
}
/**
 * A positioner for your editor. This returns an array of active positions and
 * is useful for tracking the positions of multiple items in the editor.
 *
 * ```ts
 * import { Positioner } from 'remirror/extension/positioner
 * import { useMultiPositioner } from 'remirror/react';
 *
 * const positioner = Positioner.create({
 *   ...config, // custom config
 * })
 *
 * const MenuComponent: FC = () => {
 *   const positions = usePositioner(positioner);
 *
 *   return (
 *     <>
 *       {
 *         positions.map(({ ref, bottom, left, key }) => (
 *           <div style={{ bottom, left }} ref={ref} key={key}>
 *             <MenuIcon {...options} />
 *           </div>
 *         )
 *       }
 *     </>
 *   )
 * }
 * ```
 */

function useMultiPositioner(positioner) {
  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      state = _useState4[0],
      setState = _useState4[1];

  var memoizedPositioner = useMemo(() => getPositioner(positioner), [positioner]);

  var _useState5 = useState([]),
      _useState6 = _slicedToArray(_useState5, 2),
      collectRefs = _useState6[0],
      setCollectRefs = _useState6[1];

  useExtension(PositionerExtension, useCallback(parameter => {
    var addCustomHandler = parameter.addCustomHandler;
    return addCustomHandler('positioner', memoizedPositioner);
  }, [memoizedPositioner]), []); // Add the positioner update handlers.

  useEffect(() => {
    var disposeUpdate = memoizedPositioner.addListener('update', parameter => {
      var items = [];

      var _iterator = _createForOfIteratorHelper(parameter),
          _step;

      try {
        var _loop = function _loop() {
          var _step$value = _step.value,
              id = _step$value.id,
              setElement = _step$value.setElement;

          var ref = element => {
            if (!element) {
              return;
            }

            setElement(element);
          };

          items.push({
            id,
            ref
          });
        };

        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          _loop();
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      setCollectRefs(items);
    });
    var disposeElementsAdded = memoizedPositioner.addListener('done', parameter => {
      setState(parameter);
    });
    return () => {
      disposeUpdate();
      disposeElementsAdded();
    };
  }, [memoizedPositioner]);
  return collectRefs.map((_ref, index) => {
    var _state$index;

    var ref = _ref.ref,
        key = _ref.id;

    var _ref2 = (_state$index = state[index]) !== null && _state$index !== void 0 ? _state$index : {},
        element = _ref2.element,
        position = _ref2.position;

    var virtualPosition = _objectSpread(_objectSpread({}, emptyVirtualPosition), position);

    var virtualNode = memoizedPositioner.getVirtualNode(virtualPosition);
    return _objectSpread({
      ref,
      element,
      key,
      virtualNode
    }, position);
  });
}

/**
 * A mock editor view used only when prosemirror is running on the server
 */

class EditorViewSSR {
  constructor(place, props) {
    _defineProperty(this, "dragging", null);

    var doc = getDocument('ssr');
    this.root = doc;
    this.dom = doc.createElement('div');
    this.state = props.state;
  }

  update(_props) {}

  setProps(_props) {}

  updateState(_state) {}

  someProp(_propName, f) {
    return f ? f(null) : null;
  }

  hasFocus() {
    return false;
  }

  focus() {}

  posAtCoords(_coords) {
    return null;
  }

  coordsAtPos(_pos) {
    return {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0
    };
  }

  domAtPos(_pos) {
    return {
      node: this.dom,
      offset: 0
    };
  }

  nodeDOM(_pos) {
    return null;
  }

  posAtDOM(_node, _offset, _bias) {
    return 0;
  }

  endOfTextblock(_dir, _state) {
    return true;
  }
  /**
   * Removes the editor from the DOM and destroys all [node
   * views](#view.NodeView).
   */


  destroy() {}

  dispatch(_tr) {}

}
/**
 * Creates a new editor view
 *
 * @param place
 * @param props
 * @param forceEnvironment
 */

var createEditorView = (place, props, forceEnvironment) => {
  var Constructor = shouldUseDomEnvironment(forceEnvironment) ? EditorView : Cast(EditorViewSSR);
  return new Constructor(place, props);
};

/**
 * Remirror SSR component used for rendering in non dom environments.
 */
var RemirrorSSR = (_ref) => {
  var attributes = _ref.attributes,
      manager = _ref.manager,
      state = _ref.state,
      editable = _ref.editable;
  var outerProperties = mapProps(attributes);
  var ssrElement = ReactSerializer.fromManager(manager).serializeFragment(state.doc.content);
  var transformedElement = manager.store.ssrTransformer(ssrElement, state);
  return /*#__PURE__*/React.createElement("div", _extends({}, outerProperties, {
    suppressContentEditableWarning: true,
    contentEditable: editable
  }), transformedElement);
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * The component responsible for rendering your prosemirror editor to the DOM.
 *
 * @remarks
 *
 * This is an internal component and should only be used within the `remirror`
 * codebase. The `RemirrorProvider` is the only supported way for consuming the
 * application.
 *
 * @internal
 */

var ReactEditor = props => {
  var _props$initialContent;

  var _props$stringHandler = props.stringHandler,
      stringHandler = _props$stringHandler === void 0 ? defaultStringHandler : _props$stringHandler,
      onError = props.onError,
      manager = props.manager,
      forceEnvironment = props.forceEnvironment,
      value = props.value;
  var placeholder = props.placeholder;
  var isFirstMount = useFirstMountState(); // Update the placeholder on first render.

  if (isFirstMount && !isNullOrUndefined(placeholder)) {
    manager.getPreset(ReactPreset).setOptions({
      placeholder
    });
  } // Keep the placeholder updated


  useUpdateEffect(() => {
    if (isNullOrUndefined(placeholder)) {
      return;
    }

    manager.getPreset(ReactPreset).setOptions({
      placeholder
    });
  }, [placeholder, manager]);
  var createStateFromContent = useCallback((content, selection) => {
    return manager.createState({
      content,
      doc: getDocument(forceEnvironment),
      stringHandler,
      selection,
      onError
    });
  }, [onError, forceEnvironment, manager, stringHandler]);
  var fallback = isFunction(onError) ? onError() : onError !== null && onError !== void 0 ? onError : EMPTY_PARAGRAPH_NODE;
  var initialContentArgs = isArray(props.initialContent) ? props.initialContent : [(_props$initialContent = props.initialContent) !== null && _props$initialContent !== void 0 ? _props$initialContent : fallback];
  var initialContent = initialContentArgs[0];
  var initialSelection = initialContentArgs[1];
  var initialEditorState = bool(value) ? value : createStateFromContent(initialContent, initialSelection);

  var _useState = useState(props.suppressHydrationWarning ? false : undefined),
      _useState2 = _slicedToArray(_useState, 2),
      shouldRenderClient = _useState2[0],
      setShouldRenderClient = _useState2[1]; // Store all the `logic` in a `ref`


  var methods = useEditorWrapper({
    initialEditorState,
    setShouldRenderClient,
    createStateFromContent,
    getProps: () => props,
    getShouldRenderClient: () => shouldRenderClient
  }); // Handle the initial editor mount.

  useEffect(() => {
    methods.onMount();
    return () => {
      methods.onDestroy();
    };
  }, [methods]);
  var previousEditable = usePrevious(props.editable); // Handle editor updates

  useEffect(() => {
    methods.onUpdate(previousEditable);
  }, [previousEditable, methods]);
  useControlledEditor(methods); // Return the rendered component

  return methods.render();
};
/**
 * Sets a flag to be a static remirror
 */

ReactEditor.remirrorType = RemirrorType.Editor;
/**
 * A function that takes the injected remirror params and returns JSX to render.
 *
 * @param - injected remirror params
 */

var _getShouldRenderClient = /*#__PURE__*/new WeakMap();

var _setShouldRenderClient = /*#__PURE__*/new WeakMap();

var _editorRef = /*#__PURE__*/new WeakMap();

class ReactEditorWrapper extends EditorWrapper {
  /**
   * Whether to render the client immediately.
   */

  /**
   * Update the should render client state input.
   */

  /**
   * Stores the Prosemirror EditorView dom element.
   */

  /**
   * Used when suppressHydrationWarning is true to determine when it's okay to
   * render the client content.
   */
  get shouldRenderClient() {
    return _classPrivateFieldGet(this, _getShouldRenderClient).call(this);
  }
  /**
   * Keep track of whether the get root props has been called during the most recent render.
   */


  constructor(parameter) {
    var _this$props$placehold;

    super(parameter);

    _getShouldRenderClient.set(this, {
      writable: true,
      value: void 0
    });

    _setShouldRenderClient.set(this, {
      writable: true,
      value: void 0
    });

    _editorRef.set(this, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "rootPropsConfig", {
      called: false,
      count: 0
    });

    _defineProperty(this, "getRootProps", options => {
      return this.internalGetRootProps(options, null);
    });

    _defineProperty(this, "internalGetRootProps", (options, children) => {
      // Ensure that this is the first time `getRootProps` is being called during
      // this commit phase of the .
      // invariant(!this.rootPropsConfig.called, { code: ErrorConstant.REACT_GET_ROOT_PROPS });
      this.rootPropsConfig.called = true;

      var _ref = options !== null && options !== void 0 ? options : object(),
          _ref$refKey = _ref.refKey,
          refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
          config = _objectWithoutProperties(_ref, ["refKey"]);

      return _objectSpread$1(_objectSpread$1({
        [refKey]: this.onRef,
        key: this.uid
      }, config), {}, {
        children: children !== null && children !== void 0 ? children : this.renderChildren(null)
      });
    });

    _defineProperty(this, "onRef", element => {
      if (!element) {
        return;
      }

      this.rootPropsConfig.count += 1;
      !(this.rootPropsConfig.count <= 1) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_GET_ROOT_PROPS,
        message: "Called ".concat(this.rootPropsConfig.count, " times")
      }) : invariant(false) : void 0;

      _classPrivateFieldSet(this, _editorRef, element);

      this.onRefLoad();
    });

    var getShouldRenderClient = parameter.getShouldRenderClient,
        setShouldRenderClient = parameter.setShouldRenderClient;

    _classPrivateFieldSet(this, _getShouldRenderClient, getShouldRenderClient);

    _classPrivateFieldSet(this, _setShouldRenderClient, setShouldRenderClient);

    propIsFunction(this.props.children);

    if (this.manager.view) {
      return;
    }

    this.manager.getPreset(ReactPreset).setOptions({
      placeholder: (_this$props$placehold = this.props.placeholder) !== null && _this$props$placehold !== void 0 ? _this$props$placehold : ''
    });
  }
  /**
   * This is called to update props on every render so that values don't become stale.
   */


  update(parameter) {
    super.update(parameter);
    var getShouldRenderClient = parameter.getShouldRenderClient,
        setShouldRenderClient = parameter.setShouldRenderClient;

    _classPrivateFieldSet(this, _getShouldRenderClient, getShouldRenderClient);

    _classPrivateFieldSet(this, _setShouldRenderClient, setShouldRenderClient);

    return this;
  }
  /**
   * Create the prosemirror editor view.
   */


  createView(state) {
    return createEditorView(undefined, {
      state,
      nodeViews: this.manager.store.nodeViews,
      dispatchTransaction: this.dispatchTransaction,
      attributes: () => this.getAttributes(),
      editable: () => {
        var _this$props$editable;

        return (_this$props$editable = this.props.editable) !== null && _this$props$editable !== void 0 ? _this$props$editable : true;
      }
    }, this.props.forceEnvironment);
  }
  /**
   * The external `getRootProps` that is used to spread props onto a desired
   * holder element for the prosemirror view.
   */


  /**
   * Updates the state either by calling onStateChange when it exists or
   * directly setting the internal state via a `setState` call.
   */
  updateState(parameter) {
    var state = parameter.state,
        _parameter$triggerCha = parameter.triggerChange,
        triggerChange = _parameter$triggerCha === void 0 ? true : _parameter$triggerCha,
        tr = parameter.tr,
        transactions = parameter.transactions;

    if (this.props.value) {
      var onChange = this.props.onChange;
      !onChange ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_CONTROLLED,
        message: 'You are required to provide the `onChange` handler when creating a controlled editor.'
      }) : invariant(false) : void 0;
      !triggerChange ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_CONTROLLED,
        message: 'Controlled editors do not support `clearContent` or `setContent` where `triggerChange` is `true`. Update the `value` prop instead.'
      }) : invariant(false) : void 0;

      if (!this.previousStateOverride) {
        this.previousStateOverride = this.getState();
      }

      this.onChange({
        state,
        tr
      });
      return;
    } // Update the internal prosemirror state. This happens before we update
    // the component's copy of the state.


    this.view.updateState(state);

    if (triggerChange) {
      this.onChange({
        state,
        tr
      });
    }

    this.manager.onStateUpdate({
      previousState: this.previousState,
      state,
      tr,
      transactions
    });
  }
  /**
   * Update the controlled state when the value changes and notify the extension
   * of this update.
   */


  updateControlledState(state, previousState) {
    this.previousStateOverride = previousState;
    this.view.updateState(state);
    this.manager.onStateUpdate({
      previousState: this.previousState,
      state
    });
    this.previousStateOverride = undefined;
  }
  /**
   * Adds the prosemirror view to the dom in the position specified via the
   * component props.
   */


  addProsemirrorViewToDom(element, viewDom) {
    if (this.props.insertPosition === 'start') {
      element.insertBefore(viewDom, element.firstChild);
    } else {
      element.append(viewDom);
    }
  }
  /**
   * Called once the container dom node (`this.editorRef`) has been initialized
   * after the component mounts.
   *
   * This method handles the cases where the dom is not focused.
   */


  onRefLoad() {
    !_classPrivateFieldGet(this, _editorRef) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.REACT_EDITOR_VIEW,
      message: 'Something went wrong when initializing the text editor. Please check your setup.'
    }) : invariant(false) : void 0;
    var autoFocus = this.props.autoFocus;
    this.addProsemirrorViewToDom(_classPrivateFieldGet(this, _editorRef), this.view.dom);

    if (autoFocus) {
      this.focus(autoFocus);
    }

    this.onChange();
    this.addFocusListeners();
  }

  onMount() {
    var suppressHydrationWarning = this.props.suppressHydrationWarning;

    if (suppressHydrationWarning) {
      _classPrivateFieldGet(this, _setShouldRenderClient).call(this, true);
    }
  }
  /**
   * Called for every update of the props and state.
   */


  onUpdate(previousEditable) {
    // Ensure that `children` is still a render prop
    propIsFunction(this.props.children); // Check whether the editable prop has been updated

    if (this.props.editable !== previousEditable && this.view && _classPrivateFieldGet(this, _editorRef)) {
      this.view.setProps(_objectSpread$1(_objectSpread$1({}, this.view.props), {}, {
        editable: () => {
          var _this$props$editable2;

          return (_this$props$editable2 = this.props.editable) !== null && _this$props$editable2 !== void 0 ? _this$props$editable2 : true;
        }
      }));
    }
  }

  get remirrorContext() {
    return _objectSpread$1(_objectSpread$1({}, this.editorWrapperOutput), {}, {
      getRootProps: this.getRootProps,
      portalContainer: this.manager.store.portalContainer
    });
  }
  /**
   * Checks whether this is an SSR environment and returns a child array with
   * the SSR component
   *
   * @param children
   */


  renderChildren(child) {
    var _this$props = this.props,
        forceEnvironment = _this$props.forceEnvironment,
        _this$props$insertPos = _this$props.insertPosition,
        insertPosition = _this$props$insertPos === void 0 ? 'end' : _this$props$insertPos,
        suppressHydrationWarning = _this$props.suppressHydrationWarning;
    var children = isArray(child) ? child : [child];

    if (shouldUseDomEnvironment(forceEnvironment) && (!suppressHydrationWarning || this.shouldRenderClient)) {
      return children;
    }

    var ssrElement = this.renderSSR();
    return (insertPosition === 'start' ? [ssrElement, ...children] : [...children, ssrElement]).map(addKeyToElement);
  }
  /**
   * Return a JSX Element to be used within the domless environment.
   */


  renderSSR() {
    var _this$props$editable3;

    return /*#__PURE__*/React.createElement(RemirrorSSR, {
      attributes: this.getAttributes(true),
      state: this.getState(),
      manager: this.manager,
      editable: (_this$props$editable3 = this.props.editable) !== null && _this$props$editable3 !== void 0 ? _this$props$editable3 : true
    });
  }
  /**
   * Clones the passed element when `getRootProps` hasn't yet been called.
   *
   * This method also supports rendering the children within a domless environment where necessary.
   */


  renderClonedElement(element, rootProperties) {
    var _ref2 = isArray(element) ? element : [element, null],
        _ref3 = _toArray(_ref2),
        editorElement = _ref3[0],
        other = _ref3.slice(1);

    var _getElementProps = getElementProps(editorElement),
        children = _getElementProps.children,
        rest = _objectWithoutProperties(_getElementProps, ["children"]);

    var properties = isPlainObject(rootProperties) ? _objectSpread$1(_objectSpread$1({}, rootProperties), rest) : rest;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/cloneElement(editorElement, this.internalGetRootProps(properties, this.renderChildren(children))), [...other]);
  }
  /**
   * Reset the `getRootProps` called status.
   */


  resetRender() {
    // Reset the status of roots props being called
    this.rootPropsConfig.called = false;
    this.rootPropsConfig.count = 0;
  }

  render() {
    this.resetRender();
    var element = this.props.children(_objectSpread$1({}, this.remirrorContext));

    var _getElementProps2 = getElementProps(element),
        children = _getElementProps2.children,
        properties = _objectWithoutProperties(_getElementProps2, ["children"]);

    var renderedElement;

    if (this.rootPropsConfig.called) {
      // Simply return the element as this method can never actually be called
      // within an ssr environment
      renderedElement = element;
    } else if ( // When called by a provider `getRootProps` can't actually be called until
    // the jsx is generated. Check if this is being rendered via any remirror
    // context provider. In this case `getRootProps` **must** be called by the
    // consumer.
    isRemirrorContextProvider(element) || isRemirrorProvider(element)) {
      var childAsRoot = element.props.childAsRoot;
      renderedElement = childAsRoot ? /*#__PURE__*/cloneElement(element, properties, this.renderClonedElement(children, childAsRoot)) : element;
    } else {
      renderedElement = isReactDOMElement(element) ? this.renderClonedElement(element) : /*#__PURE__*/React.createElement("div", this.internalGetRootProps(undefined, this.renderChildren(element)));
    }

    this.resetRender();
    return renderedElement;
  }

}

/**
 * A hook which creates a reference to the `ReactEditorWrapper` and updates the
 * parameters on every render.
 */
function useEditorWrapper(parameter) {
  var _useRef$current;

  var isFirstMount = useFirstMountState();
  var reactEditorWrapper = (_useRef$current = useRef(isFirstMount ? new ReactEditorWrapper(parameter) : null).current) === null || _useRef$current === void 0 ? void 0 : _useRef$current.update(parameter);
  !reactEditorWrapper ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: 'Problem with `useEditorWrapper` hook.',
    code: ErrorConstant.INTERNAL
  }) : invariant(false) : void 0;
  return reactEditorWrapper;
}
/**
 * If no string handler is provided, but the user tries to provide a string as
 * content then throw an error.
 */


function defaultStringHandler() {
   process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.REACT_EDITOR_VIEW,
    message: 'No valid string handler. In order to pass in `string` as `initialContent` to the remirror editor you must provide a valid stringHandler prop'
  }) : invariant(false) ;
}
/**
 * A hook which manages the controlled updates for the editor.
 */


function useControlledEditor(methods) {
  var value = methods.props.value; // Cache whether this is a controlled editor.

  var isControlled = useRef(bool(value)).current;
  var previousValue = usePrevious(value); // Handle controlled editor updates every time the value changes.

  useEffect(() => {
    // Check if the update is valid based on current value.
    var validUpdate = value ? isControlled === true : isControlled === false;
    !validUpdate ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.REACT_CONTROLLED,
      message: isControlled ? 'You have attempted to switch from a controlled to an uncontrolled editor. Once you set up an editor as a controlled editor it must always provide a `value` prop.' : 'You have provided a `value` prop to an uncontrolled editor. In order to set up your editor as controlled you must provide the `value` prop from the very first render.'
    }) : invariant(false) : void 0;

    if (!value) {
      return;
    }

    methods.updateControlledState(value, previousValue !== null && previousValue !== void 0 ? previousValue : undefined);
  }, [isControlled, value, methods, previousValue]);
}

/**
 * This purely exists so that we know when the remirror editor has been called
 * with a provider as opposed to directly as a render prop by the user.
 *
 * It's important because, when called directly by the user, `getRootProps` is
 * automatically called when the render prop is called. However, when called via
 * a Provider, the render prop renders the context component and it's not until
 * the element is actually rendered that the getRootProp in any nested
 * components is called.
 */
var RemirrorContextProvider = (_ref) => {
  var _ = _ref.childAsRoot,
      props = _objectWithoutProperties(_ref, ["childAsRoot"]);

  return /*#__PURE__*/React.createElement(RemirrorContext.Provider, props);
};

RemirrorContextProvider.$$remirrorType = RemirrorType.ContextProvider;
RemirrorContextProvider.defaultProps = {
  childAsRoot: false
};
/**
 * The RemirrorProvider which injects context into it's child component.
 *
 * @remarks
 * This only supports one child. At the moment if that that child is a built in
 * html string element then it is also where the prosemirror editor will be
 * injected (root element).
 *
 * These can either be consumed using React Hooks
 * - `useRemirror`
 * - `usePositioner`
 */

var RemirrorProvider = props => {
  var _ref2;

  var children = props.children,
      childAsRoot = props.childAsRoot,
      manager = props.manager,
      combined = props.combined,
      settings = props.settings,
      rest = _objectWithoutProperties(props, ["children", "childAsRoot", "manager", "combined", "settings"]);

  var reactManager = useManager((_ref2 = manager !== null && manager !== void 0 ? manager : combined) !== null && _ref2 !== void 0 ? _ref2 : [], settings);
  return /*#__PURE__*/React.createElement(ReactEditor, _extends({}, rest, {
    manager: reactManager
  }), value => {
    return /*#__PURE__*/React.createElement(RemirrorContextProvider, {
      value: value,
      childAsRoot: childAsRoot
    }, oneChildOnly(children), /*#__PURE__*/React.createElement(RemirrorPortals, {
      portalContainer: value.portalContainer
    }));
  });
};
RemirrorProvider.$$remirrorType = RemirrorType.Provider;

/**
 * A provider component for the remirror i18n helper library.
 *
 * This uses `@lingui/core` in the background. So please star and support the
 * project when you have a moment.
 */
var I18nProvider = props => {
  var _props$i18n = props.i18n,
      i18n$1 = _props$i18n === void 0 ? i18n : _props$i18n,
      _props$locale = props.locale,
      locale = _props$locale === void 0 ? 'en' : _props$locale,
      supportedLocales = props.supportedLocales,
      children = props.children;
  useEffect(() => {
    i18n$1.activate(locale, supportedLocales !== null && supportedLocales !== void 0 ? supportedLocales : [locale]);
  }, [i18n$1, locale, supportedLocales]);
  return /*#__PURE__*/React.createElement(I18nContext.Provider, {
    value: {
      i18n: i18n$1,
      locale,
      supportedLocales
    }
  }, children);
};
I18nProvider.$$remirrorType = RemirrorType.I18nProvider;

/**
 * This the `ThemeProvider`. Wrap your editor with it to customise the theming
 * of content within your editor.
 *
 * Please be aware that this wraps your component in an extra dom layer.
 */
var ThemeProvider = props => {
  var theme = props.theme,
      children = props.children,
      _props$as = props.as,
      Component = _props$as === void 0 ? 'div' : _props$as;
  return /*#__PURE__*/React.createElement(Component, {
    style: createThemeVariables(theme).styles,
    className: themeStyles
  }, children);
};
ThemeProvider.$$remirrorType = RemirrorType.ThemeProvider;

export { I18nProvider, RemirrorProvider, ThemeProvider, createReactManager, useEffectWithWarning, useExtension, useForceUpdate, useI18n, useManager, useMeasure, useMultiPositioner, usePositioner, usePreset, usePrevious, useRemirror };
